/**
 * LevNutri - Serviço de API
 * Comunicação com o backend PHP
 */

import axios from 'axios';

export const botFlowService = {
  catalog: async () => (await api.get('/bot-flow-api.php')).data,
  save: async (key, text) => (await api.put('/bot-flow-api.php?action=message', { key, text })).data,
  restore: async key => (await api.delete(`/bot-flow-api.php?action=message&key=${encodeURIComponent(key)}`)).data,
  reviews: async () => (await api.get('/bot-flow-api.php?action=reviews')).data,
  review: async (id, decision) => (await api.post('/bot-flow-api.php?action=review', { id, decision })).data,
  channel: async channel => (await api.put('/bot-flow-api.php?action=channel', channel)).data,
  pix: async pixKey => (await api.put('/bot-flow-api.php?action=pix', { pix_key: pixKey })).data,
  media: async id => (await api.get(`/bot-flow-api.php?action=media&id=${Number(id)}`, { responseType: 'blob' })).data,
};

const API_URL = process.env.REACT_APP_API_URL || 'https://levnutri.com/nutri/api';

// Instância do axios com configurações padrão
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratamento de erros.
// GETs com falha de rede ou 5xx ganham 1 retry automático (800ms) — GETs são
// idempotentes; escritas (POST/PUT/DELETE) nunca são repetidas automaticamente.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 401 na PRÓPRIA tentativa de login = credenciais inválidas — deixa o
    // componente mostrar o erro. O redirect é só para sessão expirada.
    const isLoginRequest = (error.config?.url || '').includes('auth.php');
    if (error.response?.status === 401 && !isLoginRequest) {
      // Token inválido ou expirado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(error);
    }

    const cfg = error.config || {};
    const isGet       = (cfg.method || '').toLowerCase() === 'get';
    const isTransient = !error.response || error.response.status >= 500;
    if (isGet && isTransient && !cfg._retried) {
      cfg._retried = true;
      await new Promise((r) => setTimeout(r, 800));
      return api(cfg);
    }

    return Promise.reject(error);
  }
);

// ===========================================
// AUTENTICAÇÃO
// ===========================================

export const authService = {
  // Login
  login: async (email, password) => {
    const response = await api.post('/auth.php?action=login', { email, password });
    if (response.data.success && response.data.data.token) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  // Verificar se é admin
  isAdmin: () => {
    const user = authService.getCurrentUser();
    return user?.role === 'admin';
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/auth.php?action=logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  // Obter usuário atual
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Verificar se está autenticado
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
};

// ===========================================
// PACIENTES
// ===========================================

export const patientsService = {
  // Listar todos os pacientes
  getAll: async (status = 'ativo') => {
    const response = await api.get(`/patients.php?status=${status}`);
    return response.data;
  },

  // Buscar paciente por ID
  getById: async (id) => {
    const response = await api.get(`/patients.php?id=${id}`);
    return response.data;
  },

  // Criar novo paciente
  create: async (patientData) => {
    const response = await api.post('/patients.php', patientData);
    return response.data;
  },

  // Atualizar paciente
  update: async (id, patientData) => {
    const response = await api.put(`/patients.php?id=${id}`, patientData);
    return response.data;
  },

  // Deletar paciente
  delete: async (id) => {
    const response = await api.delete(`/patients.php?id=${id}`);
    return response.data;
  },

  // Obter estatísticas do paciente
  getStats: async (id) => {
    const response = await api.get(`/patients.php?id=${id}&action=stats`);
    return response.data;
  },
};

// ===========================================
// MEDIÇÕES
// ===========================================

export const measurementsService = {
  // Listar medições de um paciente
  getByPatient: async (patientId, limit = 50) => {
    const response = await api.get(`/measurements.php?patient_id=${patientId}&limit=${limit}`);
    return response.data;
  },

  // Buscar medição por ID
  getById: async (id) => {
    const response = await api.get(`/measurements.php?id=${id}`);
    return response.data;
  },

  // Criar nova medição
  create: async (measurementData) => {
    const response = await api.post('/measurements.php', measurementData);
    return response.data;
  },

  // Atualizar medição
  update: async (id, measurementData) => {
    const response = await api.put(`/measurements.php?id=${id}`, measurementData);
    return response.data;
  },

  // Deletar medição
  delete: async (id) => {
    const response = await api.delete(`/measurements.php?id=${id}`);
    return response.data;
  },

  // Obter evolução do paciente
  getEvolution: async (patientId, limit = 10) => {
    const response = await api.get(`/measurements.php?patient_id=${patientId}&action=evolution&limit=${limit}`);
    return response.data;
  },

  // Comparar duas medições
  compare: async (id1, id2) => {
    const response = await api.get(`/measurements.php?action=compare&id1=${id1}&id2=${id2}`);
    return response.data;
  },
};

// ===========================================
// BIOIMPEDÂNCIA
// ===========================================

export const bioimpedanciaService = {
  getByPatient: async (patientId, limit = 50) => {
    const response = await api.get(`/bioimpedancia.php?patient_id=${patientId}&limit=${limit}`);
    return response.data;
  },

  getEvolution: async (patientId, limit = 20) => {
    const response = await api.get(`/bioimpedancia.php?patient_id=${patientId}&action=evolution&limit=${limit}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/bioimpedancia.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/bioimpedancia.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/bioimpedancia.php?id=${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/bioimpedancia.php?id=${id}`);
    return response.data;
  },
};

// ===========================================
// ANTROPOMETRIA
// ===========================================

export const anthropometryService = {
  getByPatient: async (patientId, limit = 50) => {
    const response = await api.get(`/anthropometry.php?patient_id=${patientId}&limit=${limit}`);
    return response.data;
  },

  getEvolution: async (patientId, limit = 20) => {
    const response = await api.get(`/anthropometry.php?patient_id=${patientId}&action=evolution&limit=${limit}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/anthropometry.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/anthropometry.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/anthropometry.php?id=${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/anthropometry.php?id=${id}`);
    return response.data;
  },
};

// ===========================================
// ANAMNESE
// ===========================================

export const anamneseService = {
  getByPatient: async (patientId) => {
    const response = await api.get(`/anamnese.php?patient_id=${patientId}`);
    return response.data;
  },

  upsert: async (data) => {
    const response = await api.post('/anamnese.php', data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/anamnese.php?id=${id}`);
    return response.data;
  },
};

// ===========================================
// PLANO ALIMENTAR — IA (GPT-4)
// ===========================================

export const dietAIService = {
  generate: async (patientId, serviceType = 'consulta_personalizada', strategy = null) => {
    const response = await api.post('/diet-ai.php', {
      patient_id:   patientId,
      service_type: serviceType,
      strategy:     strategy || undefined,
    });
    return response.data;
  },

  // Geração assíncrona: dispara o job e faz polling (sem request de 120s)
  // 422 = bloqueio de segurança: devolve {success:false, bloqueios, alertas} em vez de lançar,
  // para a tela pedir confirmação e reenviar com confirmarRiscos=true
  startGeneration: async (patientId, serviceType = 'consulta_personalizada', strategy = null, confirmarRiscos = false, refeicoes = null) => {
    try {
      const response = await api.post('/diet-ai.php?action=start', {
        patient_id:       patientId,
        service_type:     serviceType,
        strategy:         strategy || undefined,
        confirmar_riscos: confirmarRiscos || undefined,
        refeicoes:        refeicoes?.length ? refeicoes : undefined,
      });
      return response.data;
    } catch (e) {
      if (e.response?.status === 422 && e.response.data?.bloqueios) return e.response.data;
      throw e;
    }
  },

  // Metas do dia + por refeição (sem IA): { patient_id, service_type, strategy, refeicoes?, num_refeicoes? }
  preview: async (payload) => {
    const response = await api.post('/diet-ai.php?action=preview', payload);
    return response.data;
  },

  generationStatus: async (jobId) => {
    const response = await api.get(`/diet-ai.php?action=status&job_id=${jobId}`);
    return response.data;
  },
};

// ===========================================
// PLANO ALIMENTAR — CRUD
// ===========================================

export const dietPlansService = {
  getByPatient: async (patientId) => {
    const response = await api.get(`/diet-plans.php?patient_id=${patientId}`);
    return response.data;
  },

  // Envia o PDF do plano ao paciente por WhatsApp e/ou e-mail (Fase 5.4)
  enviarAoPaciente: async (planId, canais = ['whatsapp', 'email']) => {
    const response = await api.post('/enviar-plano.php', { plan_id: planId, canais });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/diet-plans.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/diet-plans.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/diet-plans.php?id=${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/diet-plans.php?id=${id}`);
    return response.data;
  },
};

// ===========================================
// DIETAS (gerador legado)
// ===========================================

export const dietService = {
  // Gerar dieta automaticamente
  generate: async (patientId, measurementId = null) => {
    const url = measurementId 
      ? `/diet-generator.php?action=generate&patient_id=${patientId}&measurement_id=${measurementId}`
      : `/diet-generator.php?action=generate&patient_id=${patientId}`;
    const response = await api.get(url);
    return response.data;
  },

  // Listar planos de um paciente
  getByPatient: async (patientId) => {
    const response = await api.get(`/diet-generator.php?patient_id=${patientId}`);
    return response.data;
  },

  // Salvar plano de dieta
  save: async (dietData) => {
    const response = await api.post('/diet-generator.php', dietData);
    return response.data;
  },
};

// ===========================================
// BIOIMPEDÂNCIA ONLINE (estimativa por IA)
// ===========================================

export const bioimpedanciaOnlineService = {
  estimate: async (data) => {
    const response = await api.post('/bioimpedancia-online.php', data);
    return response.data;
  },

  motivation: async (data) => {
    const response = await api.post('/bioimpedancia-online.php?action=motivation', data);
    return response.data;
  },
};

// ===========================================
// PAINEL ADMINISTRATIVO (admin only)
// ===========================================

export const adminService = {
  getStats: async () => {
    const response = await api.get('/admin.php?action=stats');
    return response.data;
  },

  getAllPatients: async (search = '', nutriId = null, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ action: 'patients', search, limit, offset });
    if (nutriId) params.set('nutri_id', nutriId);
    const response = await api.get(`/admin.php?${params}`);
    return response.data;
  },

  transferPatient: async (patientId, nutriId) => {
    const response = await api.post('/admin.php?action=transfer', { patient_id: patientId, nutri_id: nutriId });
    return response.data;
  },

  deletePatient: async (id) => {
    const response = await api.delete(`/admin.php?target=patient&id=${id}`);
    return response.data;
  },

  getAllUsers: async () => {
    const response = await api.get('/admin.php?action=users');
    return response.data;
  },

  createUser: async (data) => {
    const response = await api.post('/admin.php?action=user', data);
    return response.data;
  },

  updateUser: async (id, data) => {
    const response = await api.put(`/admin.php?id=${id}`, data);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/admin.php?target=user&id=${id}`);
    return response.data;
  },
};

// ===========================================
// USUÁRIOS / NUTRICIONISTAS (admin only)
// ===========================================

export const usersService = {
  getAll: async (activeOnly = false) => {
    const response = await api.get(`/users.php${activeOnly ? '?active=1' : ''}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/users.php?id=${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/users.php', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/users.php?id=${id}`, data);
    return response.data;
  },

  toggleActive: async (id, active) => {
    const response = await api.post('/users.php', { action: 'toggle', id, active });
    return response.data;
  },
};

// ===========================================
// BANCO DE ALIMENTOS E SUPLEMENTOS
// ===========================================

export const foodDatabaseService = {
  // Todos os alimentos (para carregar no editor)
  getAll: async () => {
    const response = await api.get('/food-database.php?all=1');
    return response.data;
  },

  // Alimentos por categoria
  getByCategory: async (category) => {
    const response = await api.get(`/food-database.php?category=${encodeURIComponent(category)}`);
    return response.data;
  },

  // Busca por nome
  search: async (query) => {
    const response = await api.get(`/food-database.php?search=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Todos os suplementos
  getAllSupplements: async () => {
    const response = await api.get('/food-database.php?type=supplements&all=1');
    return response.data;
  },

  // Busca de suplementos por nome
  searchSupplements: async (query) => {
    const response = await api.get(`/food-database.php?type=supplements&search=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Adicionar alimento personalizado (admin)
  addFood: async (data) => {
    const response = await api.post('/food-database.php', data);
    return response.data;
  },

  // Preferência de tabela nutricional
  getPreference: async () => {
    const response = await api.get('/food-database.php?action=get_preference');
    return response.data;
  },
  setPreference: async (preferred_source) => {
    const response = await api.post('/food-database.php', { action: 'set_preference', preferred_source });
    return response.data;
  },
};

// ===========================================
// AGENDA / CONSULTAS
// ===========================================

export const appointmentsService = {
  getByDate: async (date) => {
    const response = await api.get(`/appointments.php?date=${date}`);
    return response.data;
  },
  getByWeek: async (weekStart) => {
    const response = await api.get(`/appointments.php?week_start=${weekStart}`);
    return response.data;
  },
  getByMonth: async (month) => {
    const response = await api.get(`/appointments.php?month=${month}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/appointments.php?id=${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/appointments.php', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/appointments.php?id=${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/appointments.php?id=${id}`);
    return response.data;
  },
  getNextByPatient: async (patientId) => {
    const response = await api.get(`/appointments.php?patient_id=${patientId}&upcoming`);
    return response.data;
  },
};

// ===========================================
// FINANCEIRO
// ===========================================

export const financialService = {
  getByMonth: async (month) => {
    const response = await api.get(`/financial.php?month=${month}`);
    return response.data;
  },
  getMonthsChart: async (months = 6) => {
    const response = await api.get(`/financial.php?months=${months}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/financial.php', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/financial.php?id=${id}`);
    return response.data;
  },
};

// ===========================================
// DASHBOARD
// ===========================================

export const dashboardService = {
  getData: async () => {
    const response = await api.get('/dashboard.php');
    return response.data;
  },
};

// Inbox de pendências (Fase 4.1): o que a nutricionista precisa fazer agora
export const pendenciasService = {
  get: async () => {
    const response = await api.get('/pendencias.php');
    return response.data;
  },
};

// Leads a recuperar: quem falou com o bot mas não agendou (reativação manual)
export const leadsService = {
  get: async () => {
    const response = await api.get('/leads.php');
    return response.data;
  },
};

// App mobile do paciente — convite de ativação
export const patientAppService = {
  invite: async (patientId) => {
    const response = await api.post('/patient-auth.php?action=invite', { patient_id: patientId });
    return response.data;
  },
};

// "Esqueci minha senha" — link de redefinição via WhatsApp
export const passwordResetService = {
  forgot: async (email) => {
    const response = await api.post('/password-reset.php?action=forgot', { email });
    return response.data;
  },
  reset: async (token, password) => {
    const response = await api.post('/password-reset.php?action=reset', { token, password });
    return response.data;
  },
};

// ===========================================
// CONFIGURAÇÕES DA NUTRICIONISTA
// ===========================================

export const settingsService = {
  testSmtp: async () => (await api.post('/smtp-test.php')).data,
  get: async () => {
    const response = await api.get('/settings.php');
    return response.data;
  },
  save: async (data) => {
    const response = await api.put('/settings.php', data);
    return response.data;
  },
  uploadLogo: async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('logo', file);
    const response = await axios.post(`${API_URL}/upload-logo.php`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data;
  },
};

// ===========================================
// CUSTOS OPENAI (admin)
// ===========================================

export const openaiCostsService = {
  get: async () => {
    const response = await api.get('/openai-costs.php');
    return response.data;
  },
  saveSettings: async (data) => {
    const response = await api.post('/openai-costs.php?action=settings', data);
    return response.data;
  },
};

// ===========================================
// UPLOAD DE ARQUIVOS
// ===========================================

export const uploadService = {
  patientPhoto: async (patientId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('photo', file);
    const response = await axios.post(
      `${API_URL}/upload.php?type=patient_photo&id=${patientId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      }
    );
    return response.data;
  },
};

// ===========================================
// RELATÓRIOS CONSOLIDADOS
// ===========================================

export const reportsService = {
  sendEmail: async (patientId, email, pdf) => {
    const body = new FormData();
    body.append('patient_id', patientId);
    body.append('confirmed_email', email);
    body.append('report', pdf);
    return (await api.post('/report-email.php', body, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  getData: async (patientId) => {
    const response = await api.get(`/reports.php?patient_id=${patientId}`);
    return response.data;
  },
};

// ===========================================
// FICHAS ONLINE (ANAMNESE PÚBLICA)
// ===========================================

export const fichasService = {
  getAll: async () => {
    const response = await api.get('/anamnese-tokens.php');
    return response.data;
  },
  createToken: async (data) => {
    const response = await api.post('/anamnese-tokens.php', data);
    return response.data;
  },
  importar: async (token) => {
    const response = await api.put(`/anamnese-tokens.php?token=${token}&action=importar`);
    return response.data;
  },
  deletar: async (token) => {
    const response = await api.delete(`/anamnese-tokens.php?token=${token}`);
    return response.data;
  },
  update: async (token, data) => {
    const response = await api.patch(`/anamnese-tokens.php?token=${token}`, data);
    return response.data;
  },
  // Retorna exames anexados + sugestão de IA (null se ainda não gerada)
  getAnalise: async (token) => {
    const response = await api.get(`/analise-exames.php?token=${token}`);
    return response.data;
  },
  // Dispara geração da análise via GPT-4o
  analise: async (token) => {
    const response = await api.post('/analise-exames.php', { token });
    return response.data;
  },
};

// ===========================================
// EXAMES LABORATORIAIS
// ===========================================

export const patientExamsService = {
  getByPatient: async (patientId) => {
    const response = await api.get(`/patient-exams.php?patient_id=${patientId}`);
    return response.data;
  },
  // 2.5: tendência de marcadores ao longo do tempo
  trends: async (patientId) => {
    const response = await api.get(`/patient-exams.php?action=trends&patient_id=${patientId}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/patient-exams.php', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/patient-exams.php?id=${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/patient-exams.php?id=${id}`);
    return response.data;
  },
  parsePdf: async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('pdf', file);
    const response = await axios.post(
      `${API_URL}/patient-exams.php?action=parse_pdf`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        timeout: 90000,
      }
    );
    return response.data;
  },
};

// ===========================================
// BOT WHATSAPP
// ===========================================

export const botService = {
  getStatus: async () => {
    const response = await api.get('/bot.php?action=status');
    return response.data;
  },
  getHealth: async () => {
    const response = await api.get('/bot.php?action=health');
    return response.data;
  },
  getConversations: async () => {
    const response = await api.get('/bot.php?action=conversations');
    return response.data;
  },
  saveConfig: async (data) => {
    const response = await api.put('/bot.php?action=config', data);
    return response.data;
  },
};

// Alertas de saldo (OpenAI) para o Dashboard
export const saldoAlertasService = {
  get: async () => {
    const response = await api.get('/saldo-alertas.php');
    return response.data;
  },
};

// ===========================================
// RECOMENDAÇÕES DO PACIENTE
// ===========================================

export const recommendationsService = {
  getByPatient: async (patientId) => {
    const response = await api.get(`/recommendations.php?patient_id=${patientId}`);
    return response.data;
  },
  save: async (data) => {
    const response = await api.post('/recommendations.php', data);
    return response.data;
  },
};

// ===========================================
// ATIVIDADES DO PACIENTE (ACOMPANHAMENTO)
// ===========================================

export const patientActivitiesService = {
  getByMonth: async (patientId, month) => {
    const response = await api.get(`/patient-activities.php?patient_id=${patientId}&month=${month}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/patient-activities.php', data);
    return response.data;
  },
  toggleLike: async (id, liked) => {
    const response = await api.patch(`/patient-activities.php?id=${id}`, { liked });
    return response.data;
  },
};

export const backupService = {
  list: async () => {
    const response = await api.get('/backup-database.php');
    return response.data;
  },
  run: async () => {
    const response = await api.post('/backup-database.php');
    return response.data;
  },
};

export const emailService = {
  sendPlan: async (data) => {
    const response = await api.post('/send-plan-email.php', data);
    return response.data;
  },
  testSmtp: async () => {
    const response = await api.post('/send-plan-email.php', { _test: true });
    return response.data;
  },
};

export const foodSubstitutionsService = {
  getAll: async () => {
    const response = await api.get('/food-substitutions.php');
    return response.data;
  },
  search: async (foodName) => {
    const response = await api.get(`/food-substitutions.php?search=${encodeURIComponent(foodName)}`);
    return response.data;
  },
  save: async (data) => {
    const response = await api.post('/food-substitutions.php', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/food-substitutions.php?id=${id}`);
    return response.data;
  },
};

// ===========================================
// CARDÁPIOS PRONTOS (modelos de plano)
// ===========================================
export const dietTemplatesService = {
  // lista (meta apenas; sem meals_plan)
  getAll: async (search = '') => {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/diet-templates.php${q}`);
    return response.data;
  },
  // um modelo completo (com meals_plan) para preview/aplicar
  get: async (id) => {
    const response = await api.get(`/diet-templates.php?id=${id}`);
    return response.data;
  },
  save: async (data) => {
    const response = await api.post('/diet-templates.php', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/diet-templates.php?id=${id}`);
    return response.data;
  },
};

export const systemService = {
  getStatus: async () => {
    const response = await api.get('/system-status.php');
    return response.data;
  },
  testService: async (service) => {
    const response = await api.get(`/system-status.php?service=${service}`);
    return response.data;
  },
};

export const systemLogsService = {
  get: async ({ limit = 100, level = '', service = '' } = {}) => {
    const params = new URLSearchParams({ limit });
    if (level)   params.append('level', level);
    if (service) params.append('service', service);
    const response = await api.get(`/system-logs.php?${params}`);
    return response.data;
  },
  clear: async (service = '') => {
    const url = service
      ? `/system-logs.php?service=${encodeURIComponent(service)}`
      : '/system-logs.php';
    const response = await api.delete(url);
    return response.data;
  },
};

export default api;

// ===========================================
// DISTRIBUIÇÃO PADRÃO DE REFEIÇÕES (Configurações)
// ===========================================
export const distribuicaoRefeicoesService = {
  list: async () => (await api.get('/distribuicao-refeicoes.php')).data,
  save: async (programa, refeicoes) => (await api.put('/distribuicao-refeicoes.php', { programa, refeicoes })).data,
  restore: async (programa, numRefeicoes) =>
    (await api.delete(`/distribuicao-refeicoes.php?programa=${encodeURIComponent(programa)}&num_refeicoes=${numRefeicoes}`)).data,
};

// ===========================================
// DISTRIBUIÇÃO DE REFEIÇÕES DO PACIENTE (tem prioridade sobre o padrão)
// ===========================================
export const pacienteDistribuicaoService = {
  get: async (patientId) => (await api.get(`/paciente-distribuicao.php?patient_id=${encodeURIComponent(patientId)}`)).data,
  save: async (patientId, refeicoes) => (await api.put('/paciente-distribuicao.php', { patient_id: patientId, refeicoes })).data,
  remove: async (patientId) => (await api.delete(`/paciente-distribuicao.php?patient_id=${encodeURIComponent(patientId)}`)).data,
};
